import { escapeHtml } from '../utils/sanitize';

export interface AutocompleteItem {
  id: string;
  label: string;
}

interface SelectDetail {
  id: string | null;
  item: AutocompleteItem | null;
}

interface ClearDetail {
  previousId: string | null;
  previousItem: AutocompleteItem | null;
  query: string;
}

interface QueryDetail {
  query: string;
}

let autocompleteInstanceSeq = 0;

function isAutocompleteItemArray(value: unknown): value is AutocompleteItem[] {
  return Array.isArray(value) && value.every((item) => {
    const candidate = item as Record<string, unknown>;
    return typeof candidate.id === 'string' && typeof candidate.label === 'string';
  });
}

function safeParseItems(value: string | null): AutocompleteItem[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return isAutocompleteItemArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function setTextFieldValue(element: HTMLElement | null, value: string): void {
  if (!element) return;
  if ('value' in element) {
    (element as HTMLInputElement & { value: string }).value = value;
  }
}

function getTextFieldValue(element: EventTarget | null): string {
  if (!element || !(element instanceof HTMLElement)) return '';
  if ('value' in element) {
    return String((element as HTMLInputElement & { value: string }).value ?? '');
  }
  return '';
}

export class OpmAutocomplete extends HTMLElement {
  private _items: AutocompleteItem[] = [];
  private _selectedId: string | null = null;
  private _query = '';
  private _lastTypedQuery = '';
  private _isOpen = false;
  private _highlightedIndex = -1;
  private _renderDebounceTimer: number | null = null;
  private _searchSeq = 0;
  private readonly _uid = `opm-autocomplete-${++autocompleteInstanceSeq}`;
  private _listenersBound = false;
  private _floatingPositionListenersBound = false;
  private readonly _handleFloatingPosition = (): void => {
    this.updateFloatingDropdownPosition();
  };
  loadItems?: (query: string) => Promise<AutocompleteItem[]>;

  static get observedAttributes() {
    return ['items', 'selected-id', 'label', 'placeholder', 'disabled'];
  }

  connectedCallback(): void {
    this.parseAttributes();
    this.render();
    this.bindListeners();
    this.syncSelectionToCurrentItems();
    this.renderDropdown();
  }

  disconnectedCallback(): void {
    if (this._renderDebounceTimer !== null) {
      window.clearTimeout(this._renderDebounceTimer);
      this._renderDebounceTimer = null;
    }
    this.removeFloatingPositionListeners();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    if (name === 'items' && newValue === null) {
      this._items = [];
    }

    if (name === 'selected-id' && newValue === null) {
      this._selectedId = null;
      this._query = '';
    }

    if (name === 'disabled') {
      const textField = this.querySelector('s-text-field') as HTMLElement | null;
      if (textField) {
        if (newValue !== null && newValue !== 'false') {
          textField.setAttribute('disabled', 'true');
        } else {
          textField.removeAttribute('disabled');
        }
      }
    }

    this.parseAttributes();

    if (!this.isConnected) return;

    if (name === 'selected-id') {
      this.syncSelectionToCurrentItems();
      this.syncTextField();
    }

    this.renderDropdown();
  }

  private parseAttributes(): void {
    const itemsAttr = this.getAttribute('items');
    if (itemsAttr !== null) {
      this._items = safeParseItems(itemsAttr);
    }

    const selectedIdAttr = this.getAttribute('selected-id');
    if (selectedIdAttr !== null) {
      this._selectedId = selectedIdAttr || null;
    }

    if (this._selectedId) {
      const selectedItem = this._items.find((item) => item.id === this._selectedId);
      if (selectedItem) {
        this._query = selectedItem.label;
      }
    }
  }

  private syncSelectionToCurrentItems(): void {
    if (!this._selectedId) return;
    const selectedItem = this._items.find((item) => item.id === this._selectedId);
    if (selectedItem) {
      this._query = selectedItem.label;
      return;
    }
  }

  get selectedId(): string | null {
    return this._selectedId;
  }

  set selectedId(value: string | null) {
    this._selectedId = value;
    if (value === null) {
      this.removeAttribute('selected-id');
    } else {
      this.setAttribute('selected-id', value);
    }
    if (this.isConnected) {
      this.syncSelectionToCurrentItems();
      this.syncTextField();
      this.renderDropdown();
    }
  }

  get items(): AutocompleteItem[] {
    return this._items;
  }

  set items(value: AutocompleteItem[]) {
    this._items = Array.isArray(value) ? value : [];
    if (this.isConnected) {
      this.syncSelectionToCurrentItems();
      this.renderDropdown();
      this.syncTextField();
    }
  }

  get query(): string {
    return this._query || this._lastTypedQuery;
  }

  get lastTypedQuery(): string {
    return this._lastTypedQuery;
  }

  clear(): void {
    this._selectedId = null;
    this._query = '';
    this._lastTypedQuery = '';
    this._isOpen = false;
    this._highlightedIndex = -1;
    this.removeAttribute('selected-id');
    this.syncTextField();
    this.renderDropdown();
  }

  getFilteredItems(): AutocompleteItem[] {
    const query = this._query.toLowerCase().trim();
    if (!query) return this._items.slice(0, 15);
    return this._items.filter((item) => item.label.toLowerCase().includes(query)).slice(0, 15);
  }

  private render(): void {
    const label = escapeHtml(this.getAttribute('label') || '');
    const placeholder = escapeHtml(this.getAttribute('placeholder') || '');

    const disabledAttr = this.hasAttribute('disabled') ? 'disabled="true"' : '';

    this.innerHTML = `
      <div class="opm-autocomplete-wrapper" style="position: relative; width: 100%;">
        <s-text-field
          aria-autocomplete="list"
          aria-expanded="${this._isOpen ? 'true' : 'false'}"
          aria-controls="${this._uid}-listbox"
          aria-activedescendant="${this._highlightedIndex >= 0 ? `${this._uid}-option-${this._highlightedIndex}` : ''}"
          label="${label}"
          placeholder="${placeholder}"
          value="${escapeHtml(this._query)}"
          autocomplete="off"
          ${disabledAttr}
        ></s-text-field>
        <div
          class="opm-autocomplete-dropdown"
          id="${this._uid}-listbox"
          role="listbox"
          aria-label="${label || 'Autocomplete results'}"
          style="
            display: none;
            position: absolute;
            left: 0;
            right: 0;
            top: 100%;
            background: var(--p-color-bg-surface, #ffffff);
            border: 1px solid var(--p-color-border-subdued, #e1e3e5);
            border-radius: var(--p-border-radius-200, 8px);
            box-shadow: var(--p-shadow-200, 0 4px 6px -1px rgba(0,0,0,0.1));
            z-index: 99999;
            max-height: 260px;
            overflow-y: auto;
            margin-top: 4px;
          "
        ></div>
      </div>
    `;
  }

  private syncTextField(): void {
    const textField = this.querySelector('s-text-field') as HTMLElement | null;
    setTextFieldValue(textField, this._query);
    if (textField) {
      textField.setAttribute('aria-expanded', this._isOpen ? 'true' : 'false');
      textField.setAttribute('aria-activedescendant', this._highlightedIndex >= 0 ? `${this._uid}-option-${this._highlightedIndex}` : '');
    }
  }

  private scheduleRenderDropdown(): void {
    if (this._renderDebounceTimer !== null) {
      window.clearTimeout(this._renderDebounceTimer);
    }
    this._renderDebounceTimer = window.setTimeout(() => {
      this._renderDebounceTimer = null;
      void this.refreshItemsForQuery();
      this.renderDropdown();
    }, 120);
  }

  private async refreshItemsForQuery(): Promise<void> {
    if (typeof this.loadItems !== 'function') return;

    const seq = ++this._searchSeq;
    const nextItems = await this.loadItems(this._query);
    if (seq !== this._searchSeq) return;
    this._items = Array.isArray(nextItems) ? nextItems : [];
    this.syncSelectionToCurrentItems();
    this.renderDropdown();
  }

  private dispatchSelect(detail: SelectDetail): void {
    this.dispatchEvent(new CustomEvent<SelectDetail>('select', { detail }));
  }

  private dispatchClear(detail: ClearDetail): void {
    this.dispatchEvent(new CustomEvent<ClearDetail>('clear', { detail }));
  }

  private dispatchQueryChange(query: string): void {
    this.dispatchEvent(new CustomEvent<QueryDetail>('querychange', { detail: { query } }));
  }

  private clearSelectionFromTyping(): void {
    if (!this._selectedId && this._query.trim() === '') return;

    const previousId = this._selectedId;
    const previousItem = this._items.find((item) => item.id === previousId) || null;
    this._selectedId = null;
    this.removeAttribute('selected-id');
    this.dispatchClear({
      previousId,
      previousItem,
      query: this._query,
    });
    this.dispatchSelect({ id: null, item: null });
  }

  private selectItem(item: AutocompleteItem): void {
    this._selectedId = item.id;
    this._query = item.label;
    this._isOpen = false;
    this._highlightedIndex = -1;
    this.setAttribute('selected-id', item.id);
    this.syncTextField();
    this.renderDropdown();
    this.dispatchSelect({ id: item.id, item });
  }

  private scrollHighlightedIntoView(): void {
    const dropdown = this.querySelector('.opm-autocomplete-dropdown') as HTMLElement | null;
    if (!dropdown) return;
    const activeEl = dropdown.querySelector(`[data-idx="${this._highlightedIndex}"]`) as HTMLElement | null;
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }

  private bindListeners(): void {
    if (this._listenersBound) return;
    this._listenersBound = true;

    this.addEventListener('input', (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || target.tagName !== 'S-TEXT-FIELD') return;

      this._query = getTextFieldValue(target);
      this._lastTypedQuery = this._query;
      this._isOpen = true;
      this._highlightedIndex = -1;
      this.dispatchQueryChange(this._query);

      const selectedItem = this._items.find((item) => item.id === this._selectedId);
      if (this._selectedId && (!selectedItem || selectedItem.label.toLowerCase() !== this._query.toLowerCase())) {
        this.clearSelectionFromTyping();
      }

      this.syncTextField();
      this.scheduleRenderDropdown();
    });

    this.addEventListener('focusin', () => {
      this._isOpen = true;
      this._highlightedIndex = -1;
      this.syncTextField();
      void this.refreshItemsForQuery();
      this.renderDropdown();
    });

    this.addEventListener('focusout', (event: FocusEvent) => {
      const relatedTarget = event.relatedTarget as Node | null;
      if (relatedTarget && this.contains(relatedTarget)) {
        return;
      }

      window.setTimeout(() => {
        if (this.contains(document.activeElement)) return;

        this._isOpen = false;
        const selectedItem = this._items.find((item) => item.id === this._selectedId) || null;
        const allowCustom = this.getAttribute('allow-custom') === 'true';

        if (selectedItem) {
          this._query = selectedItem.label;
        } else if (!allowCustom) {
          this._query = '';
        }

        this.syncTextField();
        this.renderDropdown();
      }, 0);
    });

    this.addEventListener('pointerdown', (event: Event) => {
      const target = event.target as HTMLElement | null;
      const itemEl = target?.closest('.dropdown-item') as HTMLElement | null;
      if (!itemEl) return;

      event.preventDefault();
      const id = itemEl.getAttribute('data-id');
      if (!id) return;
      const item = this._items.find((candidate) => candidate.id === id);
      if (item) {
        this.selectItem(item);
      }
    });

    this.addEventListener('mousedown', (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const itemEl = target?.closest('.dropdown-item') as HTMLElement | null;
      if (!itemEl) return;

      event.preventDefault();
    });

    this.addEventListener('mouseover', (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const itemEl = target?.closest('.dropdown-item') as HTMLElement | null;
      if (!itemEl) return;

      const index = Number(itemEl.getAttribute('data-idx'));
      if (!Number.isNaN(index) && this._highlightedIndex !== index) {
        this._highlightedIndex = index;
        this.syncTextField();
        this.renderDropdown();
      }
    });

    this.addEventListener('keydown', (event: KeyboardEvent) => {
      const filtered = this.getFilteredItems();

      if (!this._isOpen) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          this._isOpen = true;
          this.renderDropdown();
          event.preventDefault();
        }
        return;
      }

      if (event.key === 'ArrowDown') {
        if (filtered.length > 0) {
          this._highlightedIndex = (this._highlightedIndex + 1) % filtered.length;
          this.renderDropdown();
          this.scrollHighlightedIntoView();
        }
        event.preventDefault();
        return;
      }

      if (event.key === 'ArrowUp') {
        if (filtered.length > 0) {
          this._highlightedIndex = (this._highlightedIndex - 1 + filtered.length) % filtered.length;
          this.renderDropdown();
          this.scrollHighlightedIntoView();
        }
        event.preventDefault();
        return;
      }

      if (event.key === 'Enter') {
        if (this._highlightedIndex >= 0 && this._highlightedIndex < filtered.length) {
          const item = filtered[this._highlightedIndex];
          if (item) this.selectItem(item);
        } else if (filtered.length > 0) {
          this.selectItem(filtered[0]!);
        }
        event.preventDefault();
        return;
      }

      if (event.key === 'Escape') {
        this._isOpen = false;
        this.renderDropdown();
        event.preventDefault();
      }
    });
  }

  private renderDropdown(): void {
    const dropdown = this.querySelector('.opm-autocomplete-dropdown') as HTMLElement | null;
    if (!dropdown) return;

    this.syncTextField();

    if (!this._isOpen) {
      this.removeFloatingPositionListeners();
      dropdown.style.display = 'none';
      this.style.position = '';
      this.style.zIndex = '';
      this.resetFloatingDropdownStyles(dropdown);
      return;
    }

    this.style.position = 'relative';
    this.style.zIndex = '10000';

    const filtered = this.getFilteredItems();

    if (filtered.length === 0) {
      dropdown.innerHTML = `
        <div style="padding: 0.75rem 1rem; color: var(--p-color-text-subdued, #6d7175); font-size: 0.875rem;">
          No results found
        </div>
      `;
      dropdown.style.display = 'block';
      this.updateFloatingDropdown(dropdown);
      return;
    }

    dropdown.innerHTML = filtered.map((item, index) => {
      const isHighlighted = index === this._highlightedIndex;
      const isSelected = item.id === this._selectedId;
      const bg = isHighlighted
        ? 'var(--p-color-bg-action-hover, rgba(0, 0, 0, 0.05))'
        : isSelected
          ? 'var(--p-color-bg-surface-selected, #f1f2f4)'
          : 'transparent';
      const fontWeight = isSelected ? '600' : '400';

      return `
        <div
          class="dropdown-item"
          id="${this._uid}-option-${index}"
          role="option"
          aria-selected="${isSelected ? 'true' : 'false'}"
          data-id="${escapeHtml(item.id)}"
          data-idx="${index}"
          style="
            padding: 0.75rem 1rem;
            cursor: pointer;
            font-size: 0.875rem;
            background: ${bg};
            font-weight: ${fontWeight};
            color: var(--p-color-text, #202223);
            border-bottom: 1px solid var(--p-color-border-subdued, #f1f2f4);
            min-height: 44px;
            display: flex;
            align-items: center;
          "
        >
          ${escapeHtml(item.label)}
        </div>
      `;
    }).join('');

    dropdown.style.display = 'block';
    this.updateFloatingDropdown(dropdown);
  }

  private isFloatingDropdownEnabled(): boolean {
    return this.hasAttribute('floating-dropdown');
  }

  private updateFloatingDropdown(dropdown: HTMLElement): void {
    if (!this.isFloatingDropdownEnabled()) {
      this.removeFloatingPositionListeners();
      this.resetFloatingDropdownStyles(dropdown);
      return;
    }

    this.addFloatingPositionListeners();
    this.updateFloatingDropdownPosition();
  }

  private updateFloatingDropdownPosition(): void {
    if (!this._isOpen || !this.isFloatingDropdownEnabled()) return;

    const dropdown = this.querySelector('.opm-autocomplete-dropdown') as HTMLElement | null;
    if (!dropdown) return;

    const rect = this.getFloatingAnchorRect(dropdown);
    if (!rect) return;
    const viewportPadding = 8;
    const dropdownWidth = Math.min(
      Math.max(rect.width, 280),
      Math.max(160, window.innerWidth - (viewportPadding * 2))
    );
    const left = Math.min(
      Math.max(rect.left, viewportPadding),
      Math.max(viewportPadding, window.innerWidth - dropdownWidth - viewportPadding)
    );
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const opensBelow = spaceBelow >= 180 || spaceBelow >= spaceAbove;
    const availableHeight = Math.max(120, Math.min(360, opensBelow ? spaceBelow : spaceAbove));

    dropdown.style.position = 'fixed';
    dropdown.style.left = `${left}px`;
    dropdown.style.right = 'auto';
    dropdown.style.width = `${dropdownWidth}px`;
    dropdown.style.maxHeight = `${availableHeight}px`;
    dropdown.style.top = opensBelow ? `${rect.bottom + 4}px` : 'auto';
    dropdown.style.bottom = opensBelow ? 'auto' : `${window.innerHeight - rect.top + 4}px`;
  }

  private getFloatingAnchorRect(dropdown: HTMLElement): DOMRect | null {
    const textField = this.querySelector('s-text-field') as HTMLElement | null;
    const textFieldRect = textField?.getBoundingClientRect();
    if (textFieldRect && textFieldRect.width > 0 && textFieldRect.height > 0) {
      return textFieldRect;
    }

    // The inner Polaris host may not expose its shadow content's box. Hide the
    // menu before measuring the outer element so the menu cannot affect its size.
    const previousDisplay = dropdown.style.display;
    dropdown.style.display = 'none';
    const anchorRect = this.getBoundingClientRect();
    dropdown.style.display = previousDisplay;
    return anchorRect.width > 0 && anchorRect.height > 0 ? anchorRect : null;
  }

  private addFloatingPositionListeners(): void {
    if (this._floatingPositionListenersBound) return;
    this._floatingPositionListenersBound = true;
    window.addEventListener('resize', this._handleFloatingPosition);
    window.addEventListener('scroll', this._handleFloatingPosition, true);
  }

  private removeFloatingPositionListeners(): void {
    if (!this._floatingPositionListenersBound) return;
    this._floatingPositionListenersBound = false;
    window.removeEventListener('resize', this._handleFloatingPosition);
    window.removeEventListener('scroll', this._handleFloatingPosition, true);
  }

  private resetFloatingDropdownStyles(dropdown: HTMLElement): void {
    if (!this.isFloatingDropdownEnabled()) return;
    dropdown.style.position = '';
    dropdown.style.left = '0';
    dropdown.style.right = '0';
    dropdown.style.width = '';
    dropdown.style.maxHeight = '260px';
    dropdown.style.top = '100%';
    dropdown.style.bottom = 'auto';
  }
}

export class OpmMultiSelect extends HTMLElement {
  private _items: AutocompleteItem[] = [];
  private _selectedIds: string[] = [];
  private _label = '';
  private _placeholder = '';
  private _listenersBound = false;
  private _autocompleteListenerBound = false;
  private _chipsListenerBound = false;

  static get observedAttributes() {
    return ['items', 'selected-ids', 'label', 'placeholder'];
  }

  connectedCallback(): void {
    this.parseAttributes();
    if (!this.querySelector('.opm-multi-select-root')) {
      this.render();
    }
    this.bindListeners();
    this.syncView();
  }

  attributeChangedCallback(_name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    if (_name === 'items' && newValue === null) {
      this._items = [];
    }

    if (_name === 'selected-ids' && newValue === null) {
      this._selectedIds = [];
    }

    this.parseAttributes();
    if (this.isConnected) {
      this.syncView();
    }
  }

  private parseAttributes(): void {
    const itemsAttr = this.getAttribute('items');
    if (itemsAttr !== null) {
      this._items = safeParseItems(itemsAttr);
    }

    const selectedIdsAttr = this.getAttribute('selected-ids');
    if (selectedIdsAttr !== null) {
      try {
        const parsed: unknown = JSON.parse(selectedIdsAttr);
        this._selectedIds = Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
      } catch {
        this._selectedIds = [];
      }
    }

    this._label = this.getAttribute('label') || '';
    this._placeholder = this.getAttribute('placeholder') || '';
  }

  get selectedIds(): string[] {
    return this._selectedIds;
  }

  set selectedIds(value: string[]) {
    this._selectedIds = Array.isArray(value) ? value : [];
    if (this.isConnected) {
      this.syncView();
    }
  }

  get items(): AutocompleteItem[] {
    return this._items;
  }

  set items(value: AutocompleteItem[]) {
    this._items = Array.isArray(value) ? value : [];
    if (this.isConnected) {
      this.syncView();
    }
  }

  private getAvailableItems(): AutocompleteItem[] {
    return this._items.filter((item) => !this._selectedIds.includes(item.id));
  }

  private render(): void {
    const label = escapeHtml(this._label);
    const placeholder = escapeHtml(this._placeholder);

    this.innerHTML = `
      <s-stack gap="small" class="opm-multi-select-root">
        ${label ? `<s-heading element="h4">${label}</s-heading>` : ''}
        <opm-autocomplete
          class="multi-select-autocomplete"
          placeholder="${placeholder}"
        ></opm-autocomplete>
        <div class="selected-chips-container" style="display: flex; flex-wrap: wrap; gap: 0.25rem; margin-top: 0.25rem;"></div>
        <div class="multi-select-empty-state"></div>
      </s-stack>
    `;
  }

  private syncView(): void {
    const autocomplete = this.querySelector('.multi-select-autocomplete') as OpmAutocomplete | null;
    const chipsContainer = this.querySelector('.selected-chips-container') as HTMLElement | null;
    const emptyState = this.querySelector('.multi-select-empty-state') as HTMLElement | null;
    const availableItems = this.getAvailableItems();

    if (autocomplete) {
      autocomplete.items = availableItems;
    }

    if (chipsContainer) {
      const selectedItems = this._selectedIds.map((id) => {
        return this._items.find((item) => item.id === id) || { id, label: 'Unknown' };
      });

      chipsContainer.innerHTML = selectedItems.map((item) => `
        <s-chip removable="true" data-id="${escapeHtml(item.id)}" style="margin: 2px;">
          ${escapeHtml(item.label)}
        </s-chip>
      `).join('');
    }

    if (emptyState) {
      emptyState.innerHTML = availableItems.length === 0
        ? '<s-banner tone="info">All items selected.</s-banner>'
        : '';
    }

  }

  private bindListeners(): void {
    if (this._listenersBound) return;
    this._listenersBound = true;

    const autocomplete = this.querySelector('.multi-select-autocomplete') as OpmAutocomplete | null;
    if (autocomplete && !this._autocompleteListenerBound) {
      this._autocompleteListenerBound = true;
      autocomplete.addEventListener('select', (event: Event) => {
        const customEvent = event as CustomEvent<SelectDetail>;
        const item = customEvent.detail?.item;
        if (!item) return;
        this.addItem(item);
      });
    }

    const chipsContainer = this.querySelector('.selected-chips-container') as HTMLElement | null;
    if (chipsContainer && !this._chipsListenerBound) {
      this._chipsListenerBound = true;
      chipsContainer.addEventListener('remove', (event: Event) => {
        const target = event.target as HTMLElement | null;
        const chip = target?.closest('s-chip') as HTMLElement | null;
        const id = chip?.getAttribute('data-id');
        if (id) {
          this.removeItem(id);
        }
      });
    }
  }

  private addItem(item: AutocompleteItem): void {
    if (this._selectedIds.includes(item.id)) return;

    this._selectedIds = [...this._selectedIds, item.id];
    this.syncView();

    const autocomplete = this.querySelector('.multi-select-autocomplete') as OpmAutocomplete | null;
    if (autocomplete) {
      autocomplete.clear();
      autocomplete.focus?.();
    }

    this.dispatchEvent(new CustomEvent('change', {
      detail: { selectedIds: [...this._selectedIds] }
    }));
  }

  private removeItem(id: string): void {
    if (!this._selectedIds.includes(id)) return;

    this._selectedIds = this._selectedIds.filter((candidate) => candidate !== id);
    this.syncView();

    this.dispatchEvent(new CustomEvent('change', {
      detail: { selectedIds: [...this._selectedIds] }
    }));
  }
}

if (!customElements.get('opm-autocomplete')) {
  customElements.define('opm-autocomplete', OpmAutocomplete);
}

if (!customElements.get('opm-multi-select')) {
  customElements.define('opm-multi-select', OpmMultiSelect);
}
